import { 
  CircleNode, CircleNodeModel, 
  RectNode, RectNodeModel, 
  PolygonNode, PolygonNodeModel 
} from "@logicflow/core";
import { getShapeStyleFuction } from '../utils/getShapeStyleUtil'
import { colors } from '../utils/config'

// ---------------------- 1. 圆形节点（支持缩放、拖拽、选中、连线）----------------------
class CustomCircleNode extends CircleNode { }

class CustomCircleModel extends CircleNodeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.r = data.properties?.r || 30; // 支持通过properties覆盖半径
    this.isResizable = true; // 允许缩放
    this.text = data.text || { value: '' }; // 节点文本
  }

  getResizeRatio() {
    return {
      min: 0.5,
      max: 2,
      keepRatio: true
    };
  }

  getNodeStyle() {
    const style = super.getNodeStyle();
    style.stroke = colors.nodeBorderColor || '#349EFF';
    style.fill = colors.nodeColor || '#E8F8FF';
    style.strokeWidth = 1;
    if (this.isSelected) {
      style.stroke = colors.selectedNodeBorderColor || '#4aa9ff';
      style.strokeWidth = 2;
    }
    const properties = this.getProperties();
    return getShapeStyleFuction(style, properties);
  }

  getTextStyle() {
    const style = super.getTextStyle();
    style.fontSize = 13;
    style.fill = '#666';
    style.textAlign = 'center';
    style.textBaseline = 'middle';
    return style;
  }
}

// ---------------------- 2. 矩形节点（支持缩放、拖拽、选中、连线，带圆角）----------------------
class CustomRectNode extends RectNode { }

class CustomRectModel extends RectNodeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.width = data.properties?.width || 100;
    this.height = data.properties?.height || 60;
    this.radius = 10; // 圆角固定
    this.isResizable = true;
    this.text = data.text || { value: '' };
  }

  getResizeRatio() {
    return {
      min: 0.5,
      max: 2,
      keepRatio: false
    };
  }

  getNodeStyle() {
    const style = super.getNodeStyle();
    style.stroke = colors.nodeBorderColor || '#349EFF';
    style.fill = colors.nodeColor || '#E8F8FF';
    style.strokeWidth = 1;
    if (this.isSelected) {
      style.stroke = colors.selectedNodeBorderColor || '#4aa9ff';
      style.strokeWidth = 2;
    }
    const properties = this.getProperties();
    return getShapeStyleFuction(style, properties);
  }

  getTextStyle() {
    const style = super.getTextStyle();
    style.fontSize = 13;
    style.fill = '#666';
    style.textAlign = 'center';
    style.textBaseline = 'middle';
    return style;
  }
}

// ---------------------- 3. 菱形节点（支持缩放、拖拽、选中、连线）----------------------
class CustomDiamondNode extends PolygonNode { }

class CustomDiamondModel extends PolygonNodeModel {
  initNodeData(data) {
    super.initNodeData(data);
    // 计算菱形顶点（根据宽高动态调整）
    const size = data.properties?.width || 60;
    this.points = [
      [0, -size/2],   // 上
      [size/2, 0],    // 右
      [0, size/2],    // 下
      [-size/2, 0]    // 左
    ];
    this.isResizable = true;
    this.text = data.text || { value: '' };
  }

  getResizeRatio() {
    return {
      min: 0.5,
      max: 2,
      keepRatio: true
    };
  }

  getNodeStyle() {
    const style = super.getNodeStyle();
    style.stroke = colors.nodeBorderColor || '#349EFF';
    style.fill = colors.nodeColor || '#E8F8FF';
    style.strokeWidth = 1;
    if (this.isSelected) {
      style.stroke = colors.selectedNodeBorderColor || '#4aa9ff';
      style.strokeWidth = 2;
    }
    const properties = this.getProperties();
    return getShapeStyleFuction(style, properties);
  }

  getTextStyle() {
    const style = super.getTextStyle();
    style.fontSize = 13;
    style.fill = '#666';
    style.textAlign = 'center';
    style.textBaseline = 'middle';
    return style;
  }
}

// ---------------------- 4. 文本节点（支持编辑、拖拽、选中）----------------------
class CustomTextNode extends RectNode { 
  // 监听文本编辑事件（视图层）
  mounted() {
    super.mounted();
    // 监听开始编辑事件
    this.graphModel.eventBus.on('text-edit:start', ({ nodeId }) => {
      if (nodeId === this.model.id) {
        this.model.isEditing = true;
        this.model.setAttributes(); // 触发样式重绘
      }
    });
    // 监听编辑结束事件
    this.graphModel.eventBus.on('text-edit:end', ({ nodeId }) => {
      if (nodeId === this.model.id) {
        this.model.isEditing = false;
        this.model.setAttributes(); // 触发样式重绘
      }
    });
  }
  // 组件销毁时移除监听
  destroyed() {
    super.destroyed();
    this.graphModel.eventBus.off('text-edit:start');
    this.graphModel.eventBus.off('text-edit:end');
  }
}

class CustomTextModel extends RectNodeModel {
  initNodeData(data) {
    super.initNodeData(data);
    this.width = data.properties?.width || 120;
    this.height = data.properties?.height || 40;
    this.radius = 4;
    this.isResizable = true;
    this.text = data.text || { 
      value: '双击编辑', 
      editable: true 
    };
    // 新增：初始化编辑状态
    this.isEditing = false;
  }

  getResizeRatio() {
    return { min: 0.5, max: 3, keepRatio: false };
  }

  getNodeStyle() {
    const style = super.getNodeStyle();
    // 核心逻辑：编辑中显示背景/边框，否则透明
    if (this.isEditing) {
      // 编辑状态：浅灰色背景 + 浅边框
      style.stroke = '#e5e7eb'; // 编辑时的边框色
      style.fill = '#f9fafb';   // 编辑时的背景色
      style.strokeWidth = 1;
    } else {
      // 非编辑状态：全透明
      style.stroke = 'transparent';
      style.fill = 'transparent';
      style.strokeWidth = 0;
      // 选中时仅显示边框（非编辑状态）
      if (this.isSelected) {
        style.stroke = colors.selectedNodeBorderColor || '#4aa9ff';
        style.strokeWidth = 1;
      }
    }
    const properties = this.getProperties();
    return getShapeStyleFuction(style, properties);
  }

  getTextStyle() {
    const style = super.getTextStyle();
    style.fontSize = 14;
    style.stroke = 'transparent';
    style.fill = '#333'; // 固定黑色文字
    style.textAlign = 'center';
    style.textBaseline = 'middle';
    return style;
  }
}


// ---------------------- 导出所有节点 ----------------------
export const CustomCircle = {
  type: "custom-circle",
  view: CustomCircleNode,
  model: CustomCircleModel
};

export const CustomRect = {
  type: "custom-rect",
  view: CustomRectNode,
  model: CustomRectModel
};

export const CustomDiamond = {
  type: "custom-diamond",
  view: CustomDiamondNode,
  model: CustomDiamondModel
};

export const CustomText = {
  type: "custom-text",
  view: CustomTextNode,
  model: CustomTextModel
};

// 默认导出矩形（保持原有引用兼容）
export default CustomRect;